import { hasS3Object } from "@/lib/aws-amplify/storage";

import { Converter } from "@/components/features/_shared";
import { convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour } from "@/components/features/_shared/validation-schema/converter";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ProductDayTripPlanFormModel } from "../../../../../type";
import type { ServerModel } from "../../type";

export const serverModelToInputModelConverter = (
	serverModel: ServerModel,
): ProductDayTripPlanFormModel["InputModel"] => {
	const { planNewItem, customEmailSetting, hotelPolicy } = serverModel;

	// 初期値が他の設定に依存する可能性がある項目は、ここで初期値を設定する
	const requiredPrepMillisecond = planNewItem.requiredPrepMillisecond || hotelPolicy?.requiredPrepMillisecond || 0;
	const emailTemplateReservation = {
		atCanceled:
			planNewItem.emailTemplateReservation?.atCanceled ?? customEmailSetting?.reservation?.atCanceled ?? "",
		atCin: planNewItem.emailTemplateReservation?.atCin ?? customEmailSetting?.reservation?.atCin ?? "",
		atCreated: planNewItem.emailTemplateReservation?.atCreated ?? customEmailSetting?.reservation?.atCreated ?? "",
		atUpdated: planNewItem.emailTemplateReservation?.atUpdated ?? customEmailSetting?.reservation?.atUpdated ?? "",
	};

	const { requiredPrepMillisecondBeforeDate, requiredPrepMillisecondBeforeHour } =
		convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour(requiredPrepMillisecond);

	const inputModel: ProductDayTripPlanFormModel["InputModel"] = {
		hotelID: planNewItem.hotelID,
		planID: planNewItem.planID,

		name: planNewItem.name,
		mainTarget: planNewItem.mainTarget,

		keyVisuals: planNewItem.keyVisuals,
		mainKeyVisual: hasS3Object(planNewItem.mainKeyVisual) ? planNewItem.mainKeyVisual : DUMMY_IMAGE.s3Object,

		availableDiscountIDs: planNewItem.availableDiscountIDs,
		availableOptionIDs: planNewItem.availableOptionIDs,
		availableRoomIDs: planNewItem.availableRoomIDs,

		billingTypeTarget: planNewItem.billingTypeTarget,
		billingTypeTerm: planNewItem.billingTypeTerm,

		checkInAvailableDayOfTheWeek: planNewItem.checkInAvailableDayOfTheWeek ?? [],

		checkInEnd: planNewItem.checkInEnd,
		checkInStart: planNewItem.checkInStart,
		checkOut: planNewItem.checkOut,

		coSleepingKidsPrice: planNewItem.coSleepingKidsPrice,

		defaultDiscountID: planNewItem.defaultDiscountID,

		doNotCountKidsAsStock: planNewItem.doNotCountKidsAsStock,

		emailTemplateReservation,

		inSale: planNewItem.inSale,
		includedMeals: planNewItem.includedMeals,
		isChangeOnlyOption: planNewItem.isChangeOnlyOption,
		isCoSleepingKidsPriceActive: planNewItem.isCoSleepingKidsPriceActive,
		isDayTrip: true,
		isHiddenHotelQuestions: planNewItem.isHiddenHotelQuestions,
		isNoAddressNeeded: planNewItem.isNoAddressNeeded,
		isPrivate: planNewItem.isPrivate,
		isRequest: planNewItem.isRequest,
		isRoomPriceFree: planNewItem.isRoomPriceFree,
		isSCConnected: planNewItem.isSCConnected,
		isShowRoomForGuest: planNewItem.isShowRoomForGuest,
		isUncancelable: planNewItem.isUncancelable,
		isUnchangeable: planNewItem.isUnchangeable,
		notSendCinMail: planNewItem.notSendCinMail,
		roomChangeDisable: planNewItem.roomChangeDisable,

		kidsPolicy: planNewItem.kidsPolicy?.map(Converter.kidsPolicyItemConverter),
		cancelPolicy: planNewItem.cancelPolicy,

		longStayParams: planNewItem.longStayParams,

		maxDailyStockNum: planNewItem.maxDailyStockNum,
		maxStockNum: planNewItem.maxStockNum,

		minimumPeopleNumPerRoom: planNewItem.minimumPeopleNumPerRoom ?? 1,
		maxPeopleNumPerRoom: planNewItem.maxPeopleNumPerRoom,

		minRoomNum: planNewItem.minRoomNum ?? 1,
		maxRoomNum: planNewItem.maxRoomNum,

		onlyUseCreditPayment: planNewItem.onlyUseCreditPayment,
		onlyUseLocalPayment: planNewItem.onlyUseLocalPayment,

		price: planNewItem.price,

		questions: planNewItem.questions,

		requiredOptionIDs: planNewItem.requiredOptionIDs,

		requiredOptionGroupIDs: planNewItem.requiredOptionGroupIDs,

		requiredPrepMillisecond,

		saleStart: planNewItem.saleStart,
		saleEnd: planNewItem.saleEnd || null,
		saleStartTime: planNewItem.saleStartTime,
		saleEndTime: planNewItem.saleEndTime || null,
		saleStartBeforeDate: planNewItem.saleStartBeforeDate,

		saleTypeForTerm: planNewItem.saleTypeForTerm,

		shortStayParams: planNewItem.shortStayParams,

		staticPriceParams: planNewItem.staticPriceParams,

		stayableDayOfTheWeek: planNewItem.stayableDayOfTheWeek ?? [],
		isHolidayStayable: planNewItem.isHolidayStayable,
		isHolidayCheckInAvailable: planNewItem.isHolidayCheckInAvailable,
		stayableEnd: planNewItem.stayableEnd || null,
		stayableStart: planNewItem.stayableStart,

		stockTypeTarget: planNewItem.stockTypeTarget,
		stockTypeTerm: planNewItem.stockTypeTerm,

		// InputModel 固有のプロパティ
		requiredPrepMillisecondBeforeDate,
		requiredPrepMillisecondBeforeHour,

		// カスタムメール設定
		atCreated: {
			text: "",
			override: false,
		},
		atUpdated: {
			text: "",
			override: false,
		},
		atCanceled: {
			text: "",
			override: false,
		},
		atCheckInBefore_00days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckInBefore_01days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckInBefore_07days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckOutAfter_01days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
	} satisfies ProductDayTripPlanFormModel["InputModel"];

	return inputModel;
};
